/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useState, useEffect } from 'react';
import { useSpring, animated } from 'react-spring';
import styled from 'styled-components';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

// Assets
import './root.module.scss';
import { theme } from '@breakroom-nx/assets';
import { ArrowNarrowLeftIcon } from 'react-native-heroicons/solid';

// Components
import { Image } from 'expo-image';
import { Center, Stack, View, Box, VStack } from 'native-base';
import { Signup } from '../signup/signup';
import { getUserData } from '@breakroom-nx/classes';

import {
  AppDescriptionContainer,
  ForgotPassword,
  IconButton,
  AnonymousSetup,
  WelcomePanel,
  InvitePanel,
  LoginContainer,
  Footer,
} from '@breakroom-nx/ui';

import { AppData } from '@breakroom-nx/helpers';
import { BlurView } from 'expo-blur';
// Redux
import {
  useAppSelector,
  useAppDispatch,
  getRegisterState,
  goBack,
  flipToPane,
  login,
} from '@breakroom-nx/redux';

import { useNavigate, useParams } from 'react-router-dom';
import Globe from '../globe/globe';
import icon from '../../assets/images/icon.webp';

export const Containter = styled.div`
  background-color: rgba(25, 15, 78, 0.78);
  backdrop-filter: blur(80px);
`;

export function Root() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { username } = useParams();

  // Redux
  const currentPane = useAppSelector(getRegisterState);
  const [loading, setLoading] = useState(true);
  const [flip, setFlip] = useState(false);

  const { transform } = useSpring({
    transform: `perspective(600px) rotateY(${flip ? -180 : 0}deg)`,
    config: { mass: 5, tension: 500, friction: 80 },
  });

  useEffect(() => {
    if (!loading) {
      setFlip((state) => !state);
    } else {
      setLoading(false);
    }
  }, [currentPane]);

  useEffect(() => {
    if (username) {
      dispatch(flipToPane('Invite'));
    }
  }, []);

  const processAuthentication = async () => {
    const auth = getAuth();
    // console.log(
    //   `(AUTH NAVIGATOR) Will Check for Current User and Get Their Data`
    // );
    onAuthStateChanged(auth, (user) => {
      if (user) {
        const uid = user.uid;
        // console.log(`(AUTH NAVIGATOR) Current user from session: ${uid}`);

        getUserData(user.uid).then((user) => {
          if (user) {
            // console.log(
            //   `(AUTH NAVIGATOR) User is Authenticated ${user.username}`
            // );
            dispatch(login(user));
            navigate(`/home`);
          } else {
            // console.log(`(AUTH NAVIGATOR) Failed to get user data`);
          }
        });
      } else {
        // User is signed out
        // console.log(`(AUTH NAVIGATOR) No current user`);
        return false;
      }
    });
  };

  function goToHome() {
    // console.log(`Go Home!!!!`);
    navigate(`/home`);
  }

  useEffect(() => {
    processAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // console.log(`Rendered Root Page`);
  });

  const paneToDisplay = (panePosition: number) => {
    switch (currentPane.at(panePosition)) {
      case 'Signup':
        return (
          <Signup
            isAnonymous={false}
            username={undefined}
            onSuccess={goToHome}
          />
        );
      case 'Login':
        return <LoginContainer onSuccess={goToHome} />;
      case 'Forgot':
        return <ForgotPassword />;
      case 'Invite':
        return <InvitePanel invitee={username} />;
      case 'Anonymous':
        return <AnonymousSetup onSuccess={goToHome} />;
      default:
        return <WelcomePanel />;
    }
  };

  return (
    <>
      <Globe autospin generateRandomData useGeolocation={false} />
      <BlurView intensity={30} style={{ overflow: 'hidden' }}>
        <View
          height={'full'}
          width={'full'}
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
        >
          <Center
            flex={1}
            py={10}
            justifyContent="center"
            alignItems="center"
            minHeight={'100vh'}
          >
            <Box
              w={{ base: '90%', md: '80%', lg: '70%' }}
              maxW="1200px"
            >
              <Box alignItems={'center'} display={{ md: 'none' }}>
                <Image
                  source={{ uri: icon }}
                  cachePolicy={'memory-disk'}
                  accessibilityLabel={`${AppData.APP_NAME} logo`}
                  contentFit={'contain'}
                  style={{
                    marginTop: '5%',
                    marginBottom: '8%',
                    width: '20%',
                    aspectRatio: 1,
                  }}
                />
              </Box>
              <Stack
                direction={{ base: 'column', md: 'row' }}
                space={6}
                justifyContent="center"
              >
                <Box
                  flex={1}
                  p={8}
                  borderRadius="xl"
                  justifyContent="center"
                  minH={{ base: 'auto', md: '300px' }}
                >
                  <AppDescriptionContainer />
                </Box>

                <Box
                  flex={1}
                  p={8}
                  justifyContent="center"
                  minH={{ base: 'auto', md: '300px' }}
                >
                  <Containter
                    as={animated.div}
                    style={{
                      transform,
                      rotateY: flip ? '180deg' : '0deg',
                      borderColor: theme.colors.surface[200],
                      borderWidth: 1,
                      borderRadius: 25,
                      padding: 30,
                    }}
                  >
                    {currentPane.at(-1) !== 'Welcome' ? (
                      <View height={10} justifyContent={'center'}>
                        <IconButton
                          position="absolute"
                          onPress={() => {
                            dispatch(goBack());
                          }}
                          icon={
                            <ArrowNarrowLeftIcon
                              color={theme.colors.primary[400]}
                              size={20}
                            />
                          }
                        />
                        <Image
                          source={{ uri: icon }}
                          cachePolicy={'memory-disk'}
                          accessibilityLabel={`${AppData.APP_NAME} icon`}
                          contentFit={'contain'}
                          style={{
                            height: '2rem',
                            aspectRatio: 1,
                          }}
                        />
                      </View>
                    ) : null}
                    {paneToDisplay(-1)}
                  </Containter>
                  <Footer />
                </Box>
              </Stack>
            </Box>
          </Center>
        </View>
      </BlurView>
    </>
  );
}

export default React.memo(Root);
